import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { ShoppingCart, Menu, X, Globe, User, LogOut, Settings, Heart, Home } from 'lucide-react';
import { markLangUserChosen, markCurrencyUserChosen } from '../utils/geoDetect';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { getItemCount, currency, setCurrency } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { productIds: wishlistIds } = useWishlist();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const languages = [
    { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
    { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' }
  ];

  const currentLangCode = (i18n.resolvedLanguage || i18n.language || 'sk').split('-')[0];
  const currentLang = languages.find(lang => lang.code === currentLangCode) || languages[0];

  // "Home" button back to the HH Group main site (title/aria-label per language)
  const HHG_URL = 'https://www.hhgroup.sk/';
  const hhgTitles = {
    sk: 'Späť na hlavný web HH Group',
    cs: 'Zpět na hlavní web HH Group',
    de: 'Zurück zur Hauptseite von HH Group',
    en: 'Back to HH Group main site',
    ru: 'На главный сайт HH Group'
  };
  const hhgTitle = hhgTitles[currentLangCode] || hhgTitles.sk;

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('i18nextLng', code); } catch (e) { /* ignore */ }
    markLangUserChosen();
    setLangMenuOpen(false);
  };

  const handleCurrencyToggle = () => {
    setCurrency(currency === 'EUR' ? 'USD' : 'EUR');
    markCurrencyUserChosen();
  };

  return (
    <header className="bg-white text-gray-800 shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4 pt-9 pb-6 xl:py-6 relative">
        {/* HH Group home button (compact, top-right corner) - below xl */}
        <a
          href={HHG_URL}
          title={hhgTitle}
          aria-label={hhgTitle}
          className="xl:hidden absolute top-1.5 right-4 z-10 inline-flex items-center gap-1.5 h-7 px-3 rounded-md border border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-700 text-xs font-semibold leading-none transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
          data-testid="hhg-home-btn-compact"
        >
          <Home className="h-3.5 w-3.5" aria-hidden="true" />
          <span>HH Group</span>
        </a>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center -ml-8">
            <div style={{ 
              height: '100px', 
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '10px',
              paddingRight: '10px'
            }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_welcome-app-440/artifacts/1g8bb7ag_Sunify%20logo%20ok..jpg" 
                alt="Sunify" 
                className="w-auto"
                style={{
                  height: '150px',
                  objectFit: 'contain',
                  transform: 'scale(1.3)',
                  transformOrigin: 'center'
                }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-700 hover:text-orange-500 transition font-medium">{t('nav.home')}</Link>
            <Link to="/catalog" className="text-gray-700 hover:text-orange-500 transition font-medium">{t('nav.catalog')}</Link>
            <Link to="/brands" className="text-gray-700 hover:text-orange-500 transition font-medium">{t('nav.brands')}</Link>
            <Link to="/services" className="text-gray-700 hover:text-orange-500 transition font-medium">{t('nav.services')}</Link>
            <Link to="/blog" className="text-gray-700 hover:text-orange-500 transition font-medium">{t('nav.blog')}</Link>
            <Link to="/gallery" className="text-gray-700 hover:text-orange-500 transition font-medium">{t('nav.gallery')}</Link>
            <Link to="/contact" className="text-gray-700 hover:text-orange-500 transition font-medium">{t('nav.contact')}</Link>
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* HH Group home button - xl and up */}
            <a
              href={HHG_URL}
              title={hhgTitle}
              aria-label={hhgTitle}
              className="hidden xl:flex items-center space-x-2 px-3 py-1.5 rounded-md border border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-700 transition text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              data-testid="hhg-home-btn"
            >
              <Home className="h-4 w-4" aria-hidden="true" />
              <span>HH Group</span>
            </a>

            {/* Currency Switcher */}
            <button
              onClick={handleCurrencyToggle}
              className="hidden md:flex items-center space-x-1 px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 transition text-gray-800"
              data-testid="currency-switcher"
              aria-label={`Currency: ${currency}`}
            >
              <span className="text-sm font-semibold w-4 text-center leading-none">
                {currency === 'USD' ? '$' : '€'}
              </span>
              <span className="text-sm font-medium">{currency}</span>
            </button>

            {/* Language Switcher (mobile) - always visible */}
            <div className="relative md:hidden">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center px-2 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 transition text-gray-800"
                data-testid="mobile-lang-toggle"
                aria-label="Language"
              >
                <span className="text-base leading-none">{currentLang.flag}</span>
                <span className="ml-1 text-xs font-semibold uppercase">{currentLang.code}</span>
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-md shadow-lg py-1 text-gray-800 z-50 border border-gray-200" data-testid="mobile-lang-menu">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-2 ${
                        currentLangCode === lang.code ? 'bg-orange-50 text-orange-700 font-semibold' : ''
                      }`}
                      data-testid={`mobile-lang-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Language Switcher */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center space-x-2 px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 transition text-gray-800"
                data-testid="desktop-lang-toggle"
              >
                <Globe className="h-4 w-4" />
                <span className="text-sm">{currentLang.flag}</span>
                <span className="text-xs font-semibold uppercase">{currentLang.code}</span>
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-800 z-50 border border-gray-200" data-testid="desktop-lang-menu">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-2 ${
                        currentLangCode === lang.code ? 'bg-orange-50 text-orange-700 font-semibold' : ''
                      }`}
                      data-testid={`desktop-lang-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User Menu / Login */}
            {isAuthenticated ? (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 transition text-gray-800"
                >
                  <User className="h-4 w-4" />
                  <span className="text-sm">{user?.name}</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-800 z-50 border border-gray-200">
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-2 text-orange-600 font-semibold"
                        data-testid="admin-link"
                      >
                        <Settings className="h-4 w-4" />
                        <span>{t('nav.admin_panel')}</span>
                      </Link>
                    )}
                    <Link
                      to="/account"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <User className="h-4 w-4" />
                      <span>{t('nav.account')}</span>
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-2 text-red-600"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden md:flex items-center space-x-2 px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition text-gray-800"
                data-testid="header-login-btn"
              >
                <User className="h-5 w-5" />
                <span>{t('nav.login')}</span>
              </Link>
            )}

            {/* Mobile account/login button — always visible between lang and cart */}
            {isAuthenticated ? (
              <Link
                to={user?.role === 'admin' ? '/admin' : '/account'}
                className="md:hidden relative p-2 rounded-md text-gray-700 hover:bg-gray-100 transition"
                data-testid="mobile-account-link"
                aria-label={t('nav.account')}
                title={user?.name}
              >
                <User className="h-5 w-5" />
                {user?.role === 'admin' && (
                  <span className="absolute -top-1 -right-1 block h-2 w-2 rounded-full bg-orange-500" />
                )}
              </Link>
            ) : (
              <Link
                to="/login"
                className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 transition"
                data-testid="mobile-login-link"
                aria-label={t('nav.login')}
                title={t('nav.login')}
              >
                <User className="h-5 w-5" />
              </Link>
            )}

            {/* Wishlist (only when authenticated) */}
            {isAuthenticated && (
              <Link
                to="/wishlist"
                className="relative p-2 rounded-md text-gray-700 hover:text-red-500 transition hidden sm:block"
                title="Wishlist"
                data-testid="header-wishlist-link"
              >
                <Heart className={`h-5 w-5 ${wishlistIds.length > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                {wishlistIds.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {wishlistIds.length}
                  </span>
                )}
              </Link>
            )}

            {/* Cart */}
            <Link
              to="/cart"
              className="relative flex items-center space-x-2 px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 transition text-white"
            >
              <ShoppingCart className="h-5 w-5" />
              {getItemCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {getItemCount()}
                </span>
              )}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-800"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 space-y-2 border-t border-gray-200 pt-4" data-testid="mobile-menu">
            <Link to="/" className="block py-2 text-gray-700 hover:text-orange-500" onClick={() => setMobileMenuOpen(false)}>{t('nav.home')}</Link>
            <Link to="/catalog" className="block py-2 text-gray-700 hover:text-orange-500" onClick={() => setMobileMenuOpen(false)}>{t('nav.catalog')}</Link>
            <Link to="/brands" className="block py-2 text-gray-700 hover:text-orange-500" onClick={() => setMobileMenuOpen(false)}>{t('nav.brands')}</Link>
            <Link to="/services" className="block py-2 text-gray-700 hover:text-orange-500" onClick={() => setMobileMenuOpen(false)}>{t('nav.services')}</Link>
            <Link to="/blog" className="block py-2 text-gray-700 hover:text-orange-500" onClick={() => setMobileMenuOpen(false)}>{t('nav.blog')}</Link>
            <Link to="/gallery" className="block py-2 text-gray-700 hover:text-orange-500" onClick={() => setMobileMenuOpen(false)}>{t('nav.gallery')}</Link>
            <Link to="/contact" className="block py-2 text-gray-700 hover:text-orange-500" onClick={() => setMobileMenuOpen(false)}>{t('nav.contact')}</Link>

            {/* Currency Switcher */}
            <div className="pt-3 border-t border-gray-200">
              <button
                onClick={handleCurrencyToggle}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-800"
                data-testid="mobile-currency-switcher"
              >
                <span className="flex items-center">
                  <span className="text-base font-semibold w-5 text-center leading-none mr-2">
                    {currency === 'USD' ? '$' : '€'}
                  </span>
                  {t('common.currency', 'Mena')}
                </span>
                <span className="font-semibold">{currency}</span>
              </button>
            </div>

            {/* Account / Login */}
            <div className="pt-3 border-t border-gray-200 space-y-2">
              {isAuthenticated ? (
                <>
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center px-3 py-2 rounded-md bg-orange-50 text-orange-700 font-semibold"
                      data-testid="mobile-admin-link"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      {t('nav.admin_panel')}
                    </Link>
                  )}
                  <Link
                    to="/account"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center px-3 py-2 rounded-md hover:bg-gray-100 text-gray-700"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {t('nav.account')}
                  </Link>
                  <Link
                    to="/wishlist"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center px-3 py-2 rounded-md hover:bg-gray-100 text-gray-700"
                  >
                    <Heart className={`h-4 w-4 mr-2 ${wishlistIds.length > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                    Wishlist
                    {wishlistIds.length > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 px-2 flex items-center justify-center">
                        {wishlistIds.length}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center px-3 py-2 rounded-md hover:bg-gray-100 text-red-600"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center px-3 py-2 rounded-md bg-orange-500 text-white"
                  data-testid="mobile-login-link"
                >
                  <User className="h-4 w-4 mr-2" />
                  {t('nav.login')}
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
